import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Radio, Select, Space, Typography, Alert, App, Table } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  purchaseOrderService,
  type EligiblePoForBaRevision,
} from '@/services/purchaseOrder.service';

const { Text } = Typography;

export type SupplierRevisionRow = {
  supplierId: string;
  supplierName: string;
  targetPoId?: string;
};

interface Props {
  open: boolean;
  loading?: boolean;
  currency: string;
  suppliers: Array<{ supplierId: string; supplierName: string }>;
  onCancel: () => void;
  onConfirm: (payload: {
    mode: 'new' | 'revise_existing';
    supplier_revisions?: Array<{ supplier_id: string; target_po_id: string }>;
    target_po_id?: string;
  }) => void;
}

/**
 * Choose New PO vs revision of an existing same-supplier PO before converting Bid Analysis awards.
 */
export const CreatePOFromBidAnalysisModeModal: React.FC<Props> = ({
  open,
  loading,
  currency,
  suppliers,
  onCancel,
  onConfirm,
}) => {
  const { message } = App.useApp();
  const [mode, setMode] = useState<'new' | 'revise_existing'>('new');
  const [rows, setRows] = useState<SupplierRevisionRow[]>([]);

  useEffect(() => {
    if (!open) return;
    setMode('new');
    setRows(
      suppliers.map((s) => ({
        supplierId: s.supplierId,
        supplierName: s.supplierName,
        targetPoId: undefined,
      }))
    );
  }, [open, suppliers]);

  const primarySupplierId = suppliers[0]?.supplierId;

  const { data: eligibleBySupplier = {}, isLoading: eligibleLoading } = useQuery({
    queryKey: ['po-eligible-ba-revision-multi', currency, suppliers.map((s) => s.supplierId).join(',')],
    queryFn: async () => {
      const map: Record<string, EligiblePoForBaRevision[]> = {};
      for (const s of suppliers) {
        map[s.supplierId] = await purchaseOrderService.listEligibleForBaRevision(
          s.supplierId,
          currency || 'AED'
        );
      }
      return map;
    },
    enabled: open && mode === 'revise_existing' && suppliers.length > 0,
  });

  const handleOk = () => {
    if (mode === 'new') {
      onConfirm({ mode: 'new' });
      return;
    }
    const revisions = rows
      .filter((r) => r.targetPoId)
      .map((r) => ({ supplier_id: r.supplierId, target_po_id: r.targetPoId! }));
    if (!revisions.length) {
      message.warning('Select at least one existing Purchase Order to revise');
      return;
    }
    if (suppliers.length === 1) {
      onConfirm({
        mode: 'revise_existing',
        target_po_id: revisions[0]!.target_po_id,
        supplier_revisions: revisions,
      });
      return;
    }
    onConfirm({ mode: 'revise_existing', supplier_revisions: revisions });
  };

  const columns = useMemo(
    () => [
      {
        title: 'Supplier',
        dataIndex: 'supplierName',
        key: 'supplierName',
      },
      {
        title: 'Existing PO',
        key: 'po',
        render: (_: unknown, record: SupplierRevisionRow) => {
          const options = (eligibleBySupplier[record.supplierId] || []).map((p) => ({
            value: p.id,
            label: `${p.po_number} · Rev ${p.revision_number} · ${p.po_status}${
              p.is_open_amendment_draft ? ' (open amendment)' : ''
            }`,
          }));
          return (
            <Select
              showSearch
              allowClear
              style={{ width: '100%' }}
              placeholder="Select PO (optional = new PO for this supplier)"
              optionFilterProp="label"
              loading={eligibleLoading}
              value={record.targetPoId}
              options={options}
              onChange={(v) =>
                setRows((prev) =>
                  prev.map((r) =>
                    r.supplierId === record.supplierId ? { ...r, targetPoId: v } : r
                  )
                )
              }
            />
          );
        },
      },
    ],
    [eligibleBySupplier, eligibleLoading]
  );

  return (
    <Modal
      title="Create Purchase Order(s)"
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText={mode === 'revise_existing' ? 'Continue' : 'Create new PO(s)'}
      destroyOnClose
      width={720}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Text>How should awarded lines be converted?</Text>
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          options={[
            { value: 'new', label: 'New Purchase Order(s)' },
            { value: 'revise_existing', label: 'Revision of existing Purchase Order' },
          ]}
        />
        {mode === 'revise_existing' && (
          <>
            <Alert
              type="info"
              showIcon
              message={`Currency ${currency || 'AED'}. Draft/Submitted POs get lines appended; Approved/Sent POs create an amendment Draft first. Leave a supplier blank to still create a new PO for that supplier.`}
            />
            {suppliers.length === 1 ? (
              <Select
                showSearch
                allowClear
                style={{ width: '100%' }}
                placeholder="Select purchase order"
                optionFilterProp="label"
                loading={eligibleLoading}
                value={rows[0]?.targetPoId}
                options={(eligibleBySupplier[primarySupplierId || ''] || []).map((p) => ({
                  value: p.id,
                  label: `${p.po_number} · Rev ${p.revision_number} · ${p.po_status}${
                    p.is_open_amendment_draft ? ' (open amendment)' : ''
                  }`,
                }))}
                onChange={(v) =>
                  setRows((prev) =>
                    prev.map((r, i) => (i === 0 ? { ...r, targetPoId: v } : r))
                  )
                }
              />
            ) : (
              <Table
                size="small"
                rowKey="supplierId"
                pagination={false}
                dataSource={rows}
                columns={columns as any}
              />
            )}
          </>
        )}
      </Space>
    </Modal>
  );
};

export default CreatePOFromBidAnalysisModeModal;
